// @ts-ignore bad importing
import type { UseTreeViewExpansionParameters as UseTreeViewExpansionParametersImport } from "@mui/x-tree-view/internals/plugins/useTreeViewExpansion/useTreeViewExpansion.types";
import type { TreeViewBaseItem } from "@mui/x-tree-view-pro";
import { Reducer } from "react";
import { FieldPath, FieldValues } from "react-hook-form";

/**
 * Props for the useRateBuilder hook.
 * @template TreeItem - The structure of tree item, must extend TreeViewBaseItem
 * @template TData - The structure of form data, defaults to FieldValues
 * @template TDefaultData - The default data of item
 */
export interface UseRateBuilderProps<TreeItem extends TreeViewBaseItem, TData extends FieldValues = FieldValues> {
  /** Initial state of the rate tree */
  initialData: History<RateTreeState<TreeItem, TData>>;
  /** Function to get the data path for a given node ID, should match with form structure */
  dataPathGetter: (id: string) => FieldPath<TData>;
  /** Optional function to generate new node IDs */
  newIdGen?: () => string;
  options?: { onStateChange?: (state: History<RateTreeState<TreeItem, TData>>) => void };
}

/**
 * Interface defining methods for tree manipulation operations
 * @template TreeItem - The structure of tree item
 */
export interface TreeMethods<TreeItem extends TreeViewBaseItem, TData extends FieldValues = FieldValues> {
  /** The current tree structure represented as an array of tree items */
  tree: TreeItem[];
  /** Array of node IDs that are currently expanded in the tree view */
  expanded: string[];
  /**
   * Expands specified nodes in the tree
   */
  expand: (nodeIds: string[], replace?: boolean) => void;
  /**
   * Retrieves a specific tree item by its ID
   */
  get: (nodeId: string) => TreeItem | null;
  /**
   * Adds a new node to the tree, generate a new id if not provided
   */
  add: (parentId: string | null, treeItem?: TreeItem, data?: TData) => void;
  /**
   * Removes a node from the tree
   */
  remove: (nodeId: string) => void;
  /**
   * Copies a node in the tree, generating a new ID for all of copied data nodes
   */
  copy: (nodeId: string) => void;
  /**
   * Moves a node to a new position in the tree
   */
  move: (nodeId: string, parentId: string | null, index?: number) => void;
  /**
   * Moves a node to a new position in the tree
   */
  set: (state: History<RateTreeState<TreeItem, TData>>) => void;
  /**
   * Gets the parent node of a specified node
   */
  getParent: (nodeId: string) => TreeItem | null;
  /**
   * Gets index withuin parent node
   */
  getIndex: (nodeId: string) => number | null;
}

/**
 * Interface defining methods for rate data manipulation
 */
export interface RateMethods<TData extends FieldValues = FieldValues> {
  /** The current rate data structure */
  data: TData;
  /** Array of field paths that have been modified in the current session, is used for focus of fields */
  changedFields: FieldPath<TData>[];
  /**
   * Updates the rate data and tracks changed fields
   */
  setData: (data: TData, changedFields?: FieldPath<TData>[]) => void;
  initialData: TData;
}

/**
 * Interface providing history control functionality for the rate builder
 * @interface RateBuilderControls
 */
export interface RateBuilderControls {
  /**
   * History-related controls and state information
   */
  history: {
    /** Reverts to the previous state in the history */
    revert: () => void;
    /** Moves forward to the next state in the history if available */
    forward: () => void;
    /** Number of steps that can be reverted (undo) */
    revertSteps: number;
    /** Number of steps that can be forwarded (redo) */
    forwardSteps: number;
  };
}

/**
 * Return type for the useRateBuilder hook, combining tree manipulation, rate data management,
 * and history control functionality.
 *
 * @template TreeItem - The structure of tree item, must extend TreeViewBaseItem
 * @template TData - The structure of form data, defaults to FieldValues
 */
export interface UseRateBuilderPropsReturn<TreeItem extends TreeViewBaseItem, TData extends FieldValues = FieldValues>
  extends TreeMethods<TreeItem, TData>,
    RateMethods<TData>,
    RateBuilderControls {}

/**
 * Represents the complete state of the rate tree, including tree structure, form data, expanded nodes, and changed fields.
 */
export interface RateTreeState<
  TreeItem extends TreeViewBaseItem = TreeViewBaseItem,
  TData extends FieldValues = FieldValues,
> {
  /** The tree structure represented as an array of tree items */
  tree: TreeItem[];
  /** The form data associated with the tree nodes */
  data: TData;
  /** Array of node IDs that are currently expanded in the tree view */
  expanded: string[];
  /** List of field paths that have been modified in the current session */
  changedFields: FieldPath<TData>[];
}

/**
 * Interface representing a history stack that tracks past, current, and future states.
 * Used for implementing undo/redo functionality in the rate builder.
 */
export interface History<T = any> {
  /** Array of past states that can be reverted to (undo) */
  past: T[];
  /** The current active state */
  current: T;
  /** Array of future states that can be restored after undo (redo) */
  future: T[];
}

export type RateTreeReducer<
  TreeItem extends TreeViewBaseItem = TreeViewBaseItem,
  TData extends FieldValues = FieldValues,
> = Reducer<History<RateTreeState<TreeItem, TData>>, RateTreeActions<TreeItem, TData>>;

/**
 * Enum defining all possible actions that can be dispatched to the rate tree reducer.
 * These actions control operations like adding, removing, copying nodes, as well as
 * handling undo/redo functionality through history management.
 */
export enum RateTreeActionTypes {
  /** Resets the entire tree state to a specified initial state, reset history too */
  Reset = "RESET",
  /** Adds a new node to the tree */
  Add = "ADD",
  /** Removes a node from the tree */
  Remove = "REMOVE",
  /** Creates a copy of an existing node */
  Copy = "COPY",
  /** Moves a node to a new position in the tree */
  Move = "MOVE",
  /** Set a new state */
  Set = "SET",
  /** Modifies the data associated with the tree */
  Modify = "MODIFY",
  /** Reverts to the previous state (undo) */
  Revert = "REVERT",
  /** Moves to the next state if available (redo) */
  Forward = "FORWARD",
  /** Expands specified nodes in the tree view, change this will not change history state */
  Expand = "EXPAND",
}
/**
 * Base interface for actions that don't require additional payload
 */
export interface ActionWithoutPayload<T extends RateTreeActionTypes> {
  type: T;
}

/**
 * Action for adding a new node to the tree
 */
export interface AddAction<
  TreeItem extends TreeViewBaseItem = TreeViewBaseItem,
  TData extends FieldValues = FieldValues,
> extends ActionWithoutPayload<RateTreeActionTypes.Add> {
  /** Parent node ID where the new node will be added, null for root level */
  parentId: string | null;
  /** Optional data for the new node, these are data for tree, not for form items */
  treeItem?: TreeItem;
  data?: TData;
}

/**
 * Action for removing a node from the tree
 */
export interface RemoveAction extends ActionWithoutPayload<RateTreeActionTypes.Remove> {
  /** ID of the node to be removed */
  nodeId: string;
}

/**
 * Action for copying a node in the tree
 */
export interface CopyAction extends ActionWithoutPayload<RateTreeActionTypes.Copy> {
  /** ID of the node to be copied */
  nodeId: string;
}

/**
 * Action for moving a node to a new position in the tree
 */
export interface MoveAction extends ActionWithoutPayload<RateTreeActionTypes.Move> {
  /** ID of the node to be moved */
  nodeId: string;
  /** ID of the new parent node, null for root level */
  parentId: string | null;
  /** Optional index position within the parent's children, if not set, item is added moved to end of list */
  index?: number;
}

/**
 * Action for resetting the entire tree state
 */
interface ResetAction<TreeItem extends TreeViewBaseItem = TreeViewBaseItem, TData extends FieldValues = FieldValues>
  extends ActionWithoutPayload<RateTreeActionTypes.Reset> {
  /** The new state to reset to */
  data: RateTreeState<TreeItem, TData>;
}

/**
 * Action for setting the entire tree state with history
 */
interface SetAction<TreeItem extends TreeViewBaseItem = TreeViewBaseItem, TData extends FieldValues = FieldValues>
  extends ActionWithoutPayload<RateTreeActionTypes.Set> {
  /** The new state to reset to */
  state: History<RateTreeState<TreeItem, TData>>;
}

/**
 * Action for modifying the data associated with the tree
 */
interface ModifyAction<TData extends FieldValues = FieldValues>
  extends ActionWithoutPayload<RateTreeActionTypes.Modify> {
  /** The new data values */
  value: TData;
  /** Optional list of field paths that have been changed, used eg for highlighting or focusing */
  changedFields?: FieldPath<TData>[];
}

/**
 * Action for reverting to the previous state (undo)
 */
type RevertAction = ActionWithoutPayload<RateTreeActionTypes.Revert>;

/**
 * Action for moving to the next state if available (redo)
 */
type ForwardAction = ActionWithoutPayload<RateTreeActionTypes.Forward>;

/**
 * Action for expanding specified nodes in the tree view
 */
export interface ExpandAction extends ActionWithoutPayload<RateTreeActionTypes.Expand> {
  /** Array of node IDs to be expanded */
  nodeIds: string[];
  /** Determine if replace entire expand state */
  replace?: boolean;
}

/**
 * Union type of all possible actions that can be dispatched to the rate tree reducer
 */
export type RateTreeActions<
  TreeItem extends TreeViewBaseItem = TreeViewBaseItem,
  TData extends FieldValues = FieldValues,
> =
  | ResetAction<TreeItem, TData>
  | AddAction<TreeItem, TData>
  | RemoveAction
  | CopyAction
  | MoveAction
  | SetAction<TreeItem, TData>
  | ModifyAction<TData>
  | RevertAction
  | ForwardAction
  | ExpandAction;
export type RateBuilderTreeViewItem = TreeViewBaseItem<{
  id: string;
  label: string;
  type?: "wrapper";
}>;

export type UseTreeViewExpansionParameters = UseTreeViewExpansionParametersImport;
